import type { ReactNode } from 'react'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { ChowThumb } from '../../features/choosechow/ChowThumb'

type FeatureVariant = 'special' | 'market'

const Style = {
  Card: styled.section`
    position: relative;
    overflow: hidden;
    background: ${minimalVars.color.bgSurface};
    color: ${minimalVars.color.textPrimary};
    border-radius: ${minimalVars.radius.lg};
    border: 1px solid ${minimalVars.color.borderSubtle};
    padding: 24px;
    display: grid;
    grid-template-columns: minmax(0, 1fr) 132px;
    align-items: start;
    gap: 24px;
    min-height: 180px;
    isolation: isolate;
    box-shadow: ${minimalVars.shadow.subtle};
    transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;

    &[data-variant="special"] {
      background: #fff7f8;
      border-color: rgba(227, 24, 55, 0.16);
    }

    &[data-variant="market"] {
      background: #f7fbf9;
      border-color: rgba(37, 99, 74, 0.16);
    }

    &:hover {
      transform: translateY(-2px);
      border-color: ${minimalVars.color.brand};
      box-shadow: ${minimalVars.shadow.medium};
    }

    /* 520px is between the hand and page stops, so it stays hand-typed. */
    @media (max-width: 520px) {
      grid-template-columns: minmax(0, 1fr) 82px;
      gap: 14px;
      min-height: 164px;
      padding: 18px;
    }
  `,
  Copy: styled.div`
    display: grid;
    gap: 8px;
    align-content: start;
    align-self: stretch;
    min-width: 0;
  `,
  Aside: styled.div`
    display: grid;
    gap: 12px;
    justify-items: end;
    align-self: start;

    @media (max-width: 520px) {
      gap: 8px;
    }
  `,
  ShowcaseSlot: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 132px;
    height: 112px;
    border-radius: ${minimalVars.radius.md};
    background: ${minimalVars.color.bgSurfaceAlt};
    border: 1px solid ${minimalVars.color.borderSubtle};
    overflow: hidden;
    pointer-events: none;
    align-self: start;

    @media (max-width: 520px) {
      width: 82px;
      height: 82px;
    }
  `,
  Eyebrow: styled.p`
    margin: 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: ${minimalVars.color.brand};
    font-size: ${minimalVars.typography.metaSize};
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
  Badges: styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 28px;
  `,
  Pill: styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: ${minimalVars.typography.metaSize};
    font-weight: 600;
    background: ${minimalVars.color.bgSurfaceAlt};
    padding: 3px 8px;
    border-radius: 10px;
    border: 1px solid ${minimalVars.color.borderSubtle};
    color: ${minimalVars.color.textSecondary};
  `,
  Title: styled.h2`
    margin: 0;
    font-family: ${minimalVars.typography.displayFamily};
    font-weight: 700;
    /* This is a peer card header, so it takes the one app-wide header size and drops 1.2px. */
    font-size: ${minimalVars.typography.h2Size};
    line-height: 1.15;
    letter-spacing: 0;
    max-width: 100%;
    overflow-wrap: anywhere;
    text-wrap: balance;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    color: ${minimalVars.color.textPrimary};

    @media (max-width: 520px) {
      font-size: 1.18rem;
      line-height: 1.2;
    }
  `,
  Subtitle: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: 1.35;
    max-width: 100%;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;

    @media (max-width: 520px) {
      font-size: ${minimalVars.typography.metaSize};
    }
  `,
  FooterRow: styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 8px;
    flex-wrap: wrap;
  `,
  Cta: styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    font: inherit;
    font-weight: 700;
    font-size: ${minimalVars.typography.captionSize};
    padding: 10px 18px;
    border-radius: 12px;
    transition: filter 160ms ease, transform 160ms ease, box-shadow 160ms ease;
    white-space: nowrap;
    text-align: left;
    box-shadow: var(--chow-shadow-cta, 0 4px 12px -2px rgba(0, 0, 0, 0.35));

    &[data-variant="solid"] {
      background: ${minimalVars.color.brand};
      color: ${minimalVars.color.textInverse};
      border: 1px solid ${minimalVars.color.brand};
      font-weight: 800;
      border-radius: ${minimalVars.radius.pill};
      box-shadow: none;
    }
    &:not([data-variant="solid"]) {
      background: ${minimalVars.color.bgSurface};
      color: ${minimalVars.color.brand};
      border: 1px solid ${minimalVars.color.borderSubtle};
      border-radius: ${minimalVars.radius.pill};
    }

    @media (hover: hover) and (pointer: fine) {
      &:hover {
        filter: brightness(1.08);
        transform: translateY(-1px) scale(1.02);
        box-shadow: var(--chow-shadow-cta-hover, 0 6px 16px -2px rgba(0, 0, 0, 0.45));
      }
    }

    &:focus-visible {
      outline: 2px solid ${minimalVars.color.brand};
      outline-offset: 2px;
    }

    @media (max-width: 520px) {
      padding: 6px 12px;
      font-size: ${minimalVars.typography.metaSize};
    }
  `,
  TimingHint: styled.span`
    font-size: ${minimalVars.typography.metaSize};
    color: ${minimalVars.color.textTertiary};
    display: inline-flex;
    align-items: center;
    gap: 4px;
  `,
}

export interface FeatureCardProps {
  variant: FeatureVariant
  eyebrow: ReactNode
  icon?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  prepTime?: string
  metaBadge?: ReactNode
  ctaLabel: string
  ctaVariant?: 'solid' | 'outline'
  onCta?: () => void
  /** Decorative showcase motif / glyph / image. */
  motif?: ReactNode
}

/** Gradient promo/feature card — the maroon "special" and green "market" blocks. */
export const FeatureCard = ({
  variant,
  eyebrow,
  icon,
  title,
  subtitle,
  prepTime,
  metaBadge,
  ctaLabel,
  ctaVariant = variant === 'special' ? 'solid' : 'outline',
  onCta,
  motif,
}: FeatureCardProps) => (
  <Style.Card data-variant={variant}>
    <Style.Copy>
      <Style.Eyebrow>
        {icon}
        {eyebrow}
      </Style.Eyebrow>
      <Style.Title>{title}</Style.Title>
      {subtitle ? <Style.Subtitle>{subtitle}</Style.Subtitle> : null}
      <Style.FooterRow>
        <Style.Cta type="button" data-variant={ctaVariant} onClick={onCta}>
          {ctaLabel} →
        </Style.Cta>
        {prepTime && <Style.TimingHint>Freshly made</Style.TimingHint>}
      </Style.FooterRow>
    </Style.Copy>
    <Style.Aside>
      <Style.Badges>
        {prepTime && <Style.Pill>{prepTime}</Style.Pill>}
        {metaBadge}
      </Style.Badges>
      {motif ? (
        <Style.ShowcaseSlot aria-hidden="true">
          {typeof motif === 'string' ? (
            <ChowThumb emoji={motif} name={typeof title === 'string' ? title : undefined} />
          ) : (
            motif
          )}
        </Style.ShowcaseSlot>
      ) : null}
    </Style.Aside>
  </Style.Card>
)
